import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { anyPass, equals } from 'ramda'
import { Box, Inline } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import toNumber from 'lodash/toNumber'

import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import { formatSize, formatSizeLabel } from '@/utils/format-size'
import { isGroup } from '@/utils/listings'
import useTourLinks from '@/features/ListingDetail/use-tour-links'
import BlurredText from '@/components/blurred-text'
import { Button } from '@/components/button-new'
import CurrencyFormatter from '@/components/currency-formatter'
import {
  BathDarkIcon,
  BedroomIcon,
  Cube,
  EntranceIcon,
  FurnishedDarkIcon,
  HeartSimpleIcon,
  PlayButtonIcon,
  ShareAlt,
  SizeIcon
} from '@/components/icons'
import { getlistingTypesValue } from '@/components/listing-type-text'

const isTrue = anyPass([equals('1'), equals(1), equals(true)])

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  margin-bottom: 0px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-top: 24px;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 30px;
`

const TitleInfoContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const AddressContainer = styled.div`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
  }
`

const MobileTitle = styled.div`
  display: none;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    display: block;
  }
`

const DesktopTitle = styled.div`
  display: block;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    display: none;
  }
`

const DetailsWrapper = styled.div`
  display: grid;
  grid-auto-columns: fit-content;
  grid-auto-flow: column;
  grid-gap: 0;
  background-color: ${colors.background};
  padding: 16px 8px;
  border-radius: 8px;
  flex-wrap: wrap;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: row;
    padding: 8px;
  }
`

const DetailBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 8px 0;

  :not(:last-child) {
    border-right: 1px solid #00000005;

    @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
      border-right: none;

      :nth-child(-n + 2) {
        border-bottom: 1px solid #00000005;
      }
    }
  }

  > svg {
    flex-shrink: 0;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    justify-content: flex-start;
    padding: 10px 24px;
  }
`

const Type = styled.h4`
  color: ${colors.secondary};
  font-size: 16px;
  font-weight: ${fontWeights.medium};
  line-height: 18px;
  margin: 0 0 5px 0;
  display: flex;
  align-items: center;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 14px;
    line-height: 16px;
    margin: 0;
    color: ${colors.grey32};
  }
`

const FavouriteIcon = styled(HeartSimpleIcon)`
  > path {
    fill: ${props => (props.$isFavourite ? colors.blue16 : 'none')};
  }
`

const TitleInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 8px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    gap: 2px;
    margin-bottom: 0;
  }
`

const AddressWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  overflow: hidden;
`

const Address = styled.div`
  display: block;
  color: ${colors.regular};
  font-size: ${fontSizes.medium};
  font-weight: 700;
  line-height: 24px;
  margin: 0;
  word-break: break-word;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
    line-height: 24px;
  }
`

const DetailText = styled.p`
  color: ${colors.regular};
  font-size: 14px;
  font-weight: ${fontWeights.medium};
  margin: 0 0 0 5px;
`

const PriceWrapper = styled.div`
  display: flex;
  align-content: center;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 6px;
`

const PriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`

const PriceStarting = styled.p`
  color: ${colors.regular};
  font-size: 14px;
  font-weight: ${fontWeights.regular};
  margin: 0;
`

const Price = styled.p`
  color: ${props => (props.color ? props.color : colors.regular)};
  font-size: 32px;
  font-weight: ${props => (props.weight ? props.weight : '600')};
  line-height: 40px;
  margin: 0;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 24px;
    line-height: 32px;
  }
`

const Frequency = styled.p`
  color: ${props => (props.color ? props.color : colors.black)};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.regular};
  line-height: 28px;
  margin: 0 0 0px 0px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
    line-height: 24px;
  }
`

const PlayIcon = styled(PlayButtonIcon)`
  color: ${props =>
    props.disabled ? vars.foregroundColor.disabled : colors.black};
`

const equalsOne = equals('1')

const formatPrice = (price, locale) => (
  <CurrencyFormatter value={price} locale={locale} />
)

const renderPrice = (listing, unit, t, locale, isListingLocked) => {
  const isRented =
    listing?.state_machine === 'rented' || isTrue(listing?.marked_as_rented)

  const hasPriceDrop =
    listing.previous_price && listing.price_change_status === 'price_down'

  if (isRented) {
    return (
      <PriceWrapper>
        <Price>{t('common.rented', 'Rented')}</Price>
      </PriceWrapper>
    )
  }

  const price = isGroup(unit)
    ? formatPrice(unit.gr_min_price, locale)
    : formatPrice(listing.price, locale)

  return (
    <PriceColumn>
      {isGroup(unit) && (
        <PriceStarting>
          {t('listing:b.listings.view.starting_at.text', 'Starting at')}...
        </PriceStarting>
      )}

      {isListingLocked ? (
        <Inline align="left" alignY="bottom" space="small">
          <BlurredText variant={{ size: 'xlarge' }}>$****</BlurredText>
          <BlurredText variant={{ size: 'large' }}>/ ****</BlurredText>
        </Inline>
      ) : (
        <PriceWrapper>
          {hasPriceDrop && (
            <Price color={vars.foregroundColor.secondary} weight="500">
              <s>{formatPrice(listing.previous_price, locale)}</s>
            </Price>
          )}
          <Price color={hasPriceDrop ? vars.foregroundColor.critical : ''}>
            {price}
          </Price>
          <Frequency color={hasPriceDrop ? vars.foregroundColor.critical : ''}>
            {`/${t(
              `b.listing.view.price_frequency_${listing.price_frequency}.text`
            )}`}
          </Frequency>
        </PriceWrapper>
      )}
    </PriceColumn>
  )
}

const MobileHeaderContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const AddressRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    margin-bottom: 0;
  }
`

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 1px solid ${colors.grey88};
  background: ${props =>
    props.disabled ? vars.backgroundColor.disabled : colors.white};
  border-radius: 6px;
  width: 150px;
  height: 48px;
  gap: 5px;
  color: ${props => props.disabled && vars.foregroundColor.disabled};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    border-radius: 64px;
    width: 44px;
    height: 44px;
  }
`

const ControlButtons = styled.div`
  display: none;
  gap: 12px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: flex;
  }
`

const StyledButton = styled(Button)`
  width: 44px;
  height: 44px;
  border-radius: 64px;
  padding: 0;

  svg {
    flex-shrink: 0;
  }
`

const SaveAndShareButtons = ({ isFavourite, handleFavourite, handleShare }) => {
  return (
    <ControlButtons>
      <StyledButton
        id="listing-details-click-favourite"
        aria-label="Add to favourites"
        onClick={handleFavourite}
        theme="secondary"
        size="unset"
      >
        <FavouriteIcon $isFavourite={isFavourite} height={20} width={20} />
      </StyledButton>
      <StyledButton
        id="listing-detail-share-click"
        aria-label="Share listing"
        theme="secondary"
        size="unset"
        onClick={handleShare}
      >
        <ShareAlt height={17} width={17} />
      </StyledButton>
    </ControlButtons>
  )
}

const ListingSummary = ({
  cityName,
  furnished,
  listings,
  listingLinks,
  unit,
  unitVideos,
  isFavourite,
  handleFavourite,
  handleShare,
  areaMeasurementUnit = 'sqft',
  isListingLocked
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const locale = router?.locale
  const unitScope = unit.unit_type_scope_txt_id
  const areaMeasurementLabel = formatSizeLabel(areaMeasurementUnit)
  const { handleVideoClick, handle3DTourClick, showVideoLink, show3dTourLink } =
    useTourLinks({
      listingLinks,
      unitVideos
    })

  const getListingTypeText = () => {
    const listingTypes = getlistingTypesValue(t)

    return listingTypes[unit.unit_type_txt_id?.toLowerCase()]?.[
      unit.unit_type_scope_txt_id?.toLowerCase()
    ]
  }

  const renderFullTitle = () => (
    <TitleWrapper>
      <TitleInfoContainer>
        <TitleInfo>
          <AddressWrapper>
            <Address>
              {renderPrice(listings, unit, t, locale, isListingLocked)}
            </Address>
          </AddressWrapper>
          <Type>
            <Trans
              i18nKey="listing:b.listing.view.listing_type.text"
              values={{ listingType: getListingTypeText(), cityName }}
              defaults="{{ listingType }} for Rent {{ cityName }}"
            />
          </Type>
        </TitleInfo>
        <SaveAndShareButtons
          isFavourite={isFavourite}
          handleFavourite={handleFavourite}
          handleShare={handleShare}
        />
      </TitleInfoContainer>
      {(showVideoLink || show3dTourLink) && (
        <Box display="flex" alignItems="center" style={{ gap: '16px' }}>
          {showVideoLink && (
            <ControlButton
              onClick={handleVideoClick}
              theme="secondary"
              width="150px"
              size="unset"
              disabled={isListingLocked}
            >
              <PlayIcon height={18} width={18} disabled={isListingLocked} />
              {t('common.watch_video', 'Watch Video')}
            </ControlButton>
          )}
          {show3dTourLink && (
            <ControlButton
              onClick={handle3DTourClick}
              theme="secondary"
              width="150px"
              size="unset"
              disabled={isListingLocked}
            >
              <Cube height={18} width={18} />
              {t('common.3d_tour', '3D Tour')}
            </ControlButton>
          )}
        </Box>
      )}
    </TitleWrapper>
  )

  const renderMobileTitle = () => (
    <TitleWrapper>
      <MobileHeaderContainer>
        <AddressContainer>
          <AddressRow>
            <Address>
              {renderPrice(listings, unit, t, locale, isListingLocked)}
            </Address>
            <SaveAndShareButtons
              isFavourite={isFavourite}
              handleFavourite={handleFavourite}
              handleShare={handleShare}
            />
          </AddressRow>
          <Type>{getListingTypeText()}</Type>
        </AddressContainer>
      </MobileHeaderContainer>
    </TitleWrapper>
  )

  const getBedrooms = () => {
    if (unitScope === 'single')
      return `${
        formatSize(areaMeasurementUnit, unit.size) || '---'
      } ${areaMeasurementLabel} ${t('common.bedroom', 'Bedroom')}`
    const count = toNumber(unit.count_bedrooms)
    if (count === 0) return t('common.studio')
    return t('listing:b.listing.view.count_bedrooms.text', {
      count,
      defaultValue: '{{count}} Bed',
      defaultValue_plural: '{{count}} Beds'
    })
  }

  const getBathrooms = () => {
    if (unitScope === 'entire')
      return t('listing:b.listing.view.count_bathrooms.text', {
        count: toNumber(unit.count_all_bathrooms),
        defaultValue: '{{count}} Bath',
        defaultValue_plural: '{{count}} Baths'
      })
    const count = toNumber(unit.count_full_bathrooms)
    return t('listing:b.listing.view.count_private_bathrooms.text', {
      count,
      defaultValue: '{{count}} Private Bath'
    })
  }

  const getSize = () => {
    const { gr_min_size, gr_max_size, size } = unit

    const getGroupListingSize = () => {
      if (!gr_min_size) return `--- ${areaMeasurementLabel}`
      const minSize = formatSize(areaMeasurementUnit, gr_min_size)
      const maxSize = formatSize(areaMeasurementUnit, gr_max_size)
      const isSameSize = gr_min_size === gr_max_size
      return isSameSize
        ? `${minSize} ${areaMeasurementLabel}`
        : `${minSize}-${maxSize} ${areaMeasurementLabel}`
    }

    return isGroup(unit)
      ? getGroupListingSize()
      : `${
          formatSize(areaMeasurementUnit, size) || '---'
        } ${areaMeasurementLabel}`
  }

  const renderDetailBoxes = () => (
    <DetailsWrapper>
      <DetailBox>
        <BedroomIcon width={16} height={16} />
        <DetailText>{getBedrooms()}</DetailText>
      </DetailBox>
      {toNumber(unit.count_full_bathrooms) > 0 && (
        <DetailBox>
          <BathDarkIcon width={16} height={16} />
          <DetailText>{getBathrooms()}</DetailText>
        </DetailBox>
      )}
      {unitScope !== 'single' && (
        <DetailBox>
          <SizeIcon width={16} height={16} />
          <DetailText>{getSize()}</DetailText>
        </DetailBox>
      )}
      <DetailBox>
        <FurnishedDarkIcon width={16} height={16} />
        <DetailText>{furnished}</DetailText>
      </DetailBox>

      {equalsOne(unit.private_entrance) && unitScope === 'partial' && (
        <DetailBox>
          <EntranceIcon width={16} height={16} />
          <DetailText>
            {t(
              'listing:b.listing.view.private_entrance.text',
              'Private Entrance'
            )}
          </DetailText>
        </DetailBox>
      )}
    </DetailsWrapper>
  )
  const renderBlurredDetailBoxes = () => (
    <DetailsWrapper>
      <DetailBox>
        <BedroomIcon width={16} height={16} />
        <Box marginLeft="small">
          <BlurredText>* Beds</BlurredText>
        </Box>
      </DetailBox>
      {toNumber(unit.count_full_bathrooms) > 0 && (
        <DetailBox>
          <BathDarkIcon width={16} height={16} />
          <Box marginLeft="small">
            <BlurredText>* Baths</BlurredText>
          </Box>
        </DetailBox>
      )}
      {unitScope !== 'single' && (
        <DetailBox>
          <SizeIcon width={16} height={16} />
          <Box marginLeft="small">
            <BlurredText>* ft&sup2;</BlurredText>
          </Box>
        </DetailBox>
      )}
      <DetailBox>
        <FurnishedDarkIcon width={16} height={16} />
        <Box marginLeft="small">
          <BlurredText>****</BlurredText>
        </Box>
      </DetailBox>
    </DetailsWrapper>
  )

  return (
    <Wrapper>
      <MobileTitle>{renderMobileTitle()}</MobileTitle>
      <DesktopTitle>{renderFullTitle()}</DesktopTitle>
      {isListingLocked ? renderBlurredDetailBoxes() : renderDetailBoxes()}
    </Wrapper>
  )
}

export default ListingSummary
